// ---------------------------------------------------------------------------
// Experiment Configs — type-safe experiment definitions
// ---------------------------------------------------------------------------
//
// When adding a new experiment, define its config here. The `variations` tuple
// tells TypeScript exactly which variation strings are valid. The `useExperiment`
// hook uses this to narrow the returned `variation` field so you get
// autocomplete and compile-time errors if you check for a variation that
// doesn't exist.
//
// Common variation patterns:
//   - Standard A/B:    ['control', 'on'] DEFAULT
//   - Multi-variant:   ['control', 'variant_1', 'variant_2']
// ---------------------------------------------------------------------------

/**
 * A type-safe experiment configuration that links an experiment ID
 * to its known variation strings.
 */
export interface ExperimentConfig<
  TId extends string = string,
  TVars extends ReadonlyArray<string> = ReadonlyArray<string>,
> {
  /** The Optimizely experiment ID. */
  readonly id: TId
  /** The exhaustive list of variation strings for this experiment. */
  readonly variations: TVars
}

/** Default variations for standard A/B experiments. */
const DEFAULT_VARIATIONS = ['control', 'on'] as const

/** Helper to create an ExperimentConfig with full type inference. */
function defineExperiment<
  TId extends string,
  const TVars extends ReadonlyArray<string>,
>(id: TId, variations: TVars): ExperimentConfig<TId, TVars>
function defineExperiment<TId extends string>(id: TId): ExperimentConfig<TId, typeof DEFAULT_VARIATIONS>
function defineExperiment<
  TId extends string,
  const TVars extends ReadonlyArray<string>,
>(id: TId, variations?: TVars) {
  return { id, variations: variations ?? DEFAULT_VARIATIONS }
}

/**
 * Type-safe experiment configs.
 *
 * Pass these to `useExperiment()` instead of raw experiment IDs to get typed
 * `variation` in the result — preventing typos and making
 * exhaustive checks possible.
 *
 * Variations default to `['control', 'on']`. Only specify variations when the
 * experiment uses non-standard ones (e.g. multi-variant).
 *
 * Usage:
 * ```ts
 * // Standard A/B — variations default to ['control', 'on']
 * const { isAnyVariant } = useExperiment(Experiments.myExperiment)
 *
 * // Multi-variant — specify variations explicitly
 * const { variation } = useExperiment(Experiments.myMultiVariant)
 * // variation: 'control' | 'variant_a' | 'variant_b' | undefined
 * ```
 */
export const Experiments = {
  isSupportAssistantEnabled: defineExperiment('opex_support_assistant_customer_pool'),
  emailChangeEnabled: defineExperiment('opex_email_change'),
  securePhoneNumberChangeEnabled: defineExperiment('csx_secure_phone_number_change'),
  croLPFreePreviewAfterSignUpLogIn: defineExperiment(
    'cro_offer_lp_free_preview_after_sign_up_log_in',
    ['control', 'variant_1', 'variant_2'] as const,
  ),
  croAlwaysOnNewMobileBooking: defineExperiment('cro_lpc_new_mobile_booking'),
  croFlexibleFlightFares: defineExperiment('cro_flexible_flight_fares'),
  paymentsDepositOptionForHotelsAbove1999V2: defineExperiment('payments_deposit_option_for_hotels_above_1999_v2'),
  carHireBundledDiscountUpcomingOrderHomepageCarousel: defineExperiment('car_hire_bundled_discount_upcoming_order_homepage_carousel'),
  croPopularOptionBookingRemaining: defineExperiment('cro_popular_option_booking_remaining'),
  growthShowOfferUrgencyCountdownLabel: defineExperiment('growth_force_show_offer_urgency_countdown_label'),
  croBundledFlexibleFlightFaresV2: defineExperiment('cro_bundled_flexible_flight_fares_v2'),
  croHideHomepageSearchEmergingMarkets: defineExperiment('cro_hide_homepage_search_emerging_markets'),
  lereNonAUTwoTowersHomePageHeroCarouselV2: defineExperiment('lere_non_au_two_towers_home_page_hero_carousel_v2'),
  lerePersonalisedHomePageHeroCarousel: defineExperiment('lere_personalised_home_page_hero_carousel'),
  lerePersonalisedHomePageHeroCarouselV4: defineExperiment('lere_home_page_carousel_v4'),
  lerePersonalisedHomePageHeroCarouselV5: defineExperiment('lere_home_page_carousel_v5'),
  croPrefetchInflowCheckoutExperiences: defineExperiment('cro_prefetch_inflow_checkout_experiences'),
  croPrefetchFlightsFromOffer: defineExperiment('cro_prefetch_flights_from_offer'),
  croTrustIndicatorsSearchPageV2: defineExperiment('cro_trust_indicators_search_page_v2'),
  croShareSocietePoints: defineExperiment('cro_share_societe_points', ['control', 'variant_10', 'variant_50'] as const),
  croOfferCtaSeeDeal: defineExperiment(
    'cro_offer_cta_see_deal',
    ['control', 'variant'] as const,
  ),
  carHireObjectiveIntentMessage: defineExperiment('car_hire_objective_intent_message'),
  croFlexCfmrBedbankPackages: defineExperiment('cro_flex_cfmr_bedbank_packages'),
  growthMetasearchPerNightPricing: defineExperiment('growth_metasearch_per_night_pricing'),
  growthMetasearchRecommendedNearbyOffers: defineExperiment('growth_metasearch_recommended_nearby_offers'),
  croOrderDownloadPdf: defineExperiment('cro_order_download_pdf'),
  cruiseAutoFocusNextFilter: defineExperiment('cruise_auto_focus_next_filter'),
  cruiseMpvRanking: defineExperiment('cruise_mpv_ranking'),
  labnableVideoBackground: defineExperiment('lab_enable_video_background'),
  loyaltyCommSubBonus: defineExperiment(
    'loyalty_communication_resubscription_bonus',
    ['control', '500_points', '750_points'] as const,
  ),
  croRentalsDefaultToSplitMapSearch: defineExperiment('cro_rentals_default_to_split_map_search'),
  navHomesVillasBeforeMaldivesBundles: defineExperiment('nav_homes_villas_before_maldives_bundles'),
  growthPaidOfferGalleryVariant: defineExperiment('growth_paid_offer_gallery_variant'),
  croExpressCheckoutAutoDefault: defineExperiment('cro_express_checkout_auto_default'),
  croCheckoutPaymentOptions: defineExperiment('cro_checkout_payment_options'),
  croCheckoutIncompleteInformationModal: defineExperiment('cro_checkout_incomplete_information_modal'),
  seoStaticHotelsDynamicResolveAb: defineExperiment('seo_static_hotels_dynamic_resolve_ab'),
  croInsuranceTestimonials: defineExperiment('cro_insurance_testimonials'),
  luxPlusReconsiderCheckoutBanner: defineExperiment('lux_plus_reconsider_checkout_banner'),
  croLppCustomContentV2: defineExperiment('cro_lpp_custom_content_v2'),
  searchNlsLlmRerank: defineExperiment('search_nls_llm_rerank'),
  croDisplayLtleOnHomepage: defineExperiment('cro_display_ltle_on_homepage'),
  croTrustpilotReviewFeedInOfferPage: defineExperiment('cro_trustpilot_review_feed_in_offer_page'),
  croCaptureBouncingCustomersWithYamlModalV2: defineExperiment('cro_capture_bouncing_customers_with_yaml_modal_v2'),
  croPopularFlightsHomePage: defineExperiment('cro_popular_flights_home_page'),
  experiencesCollections: defineExperiment('experiences_collections'),
  lereRankerClustering: defineExperiment('lere_ranker_clustering'),
  opexLESAEmptyChatFreeText: defineExperiment('opex_lesa_empty_chat_free_text_ab_test'),
  opexBookingAuthorityReferralBannerAbTest: defineExperiment(
    'opex_booking_authority_referral_banner_ab_test_v2',
    ['control', 'variant_with_referral_banner', 'referral_banner_variant_no_t_and_c'] as const,
  ),
  croChildrenStayFreeTag: defineExperiment('cro_children_stay_free_tag'),
  // Funnel optimiser A/B tests (see src/checkout/selectors/funnel.ts for usage)
  funnelOptimiserRuleId16: defineExperiment('funnel_optimiser_ruleid_16'),
  funnelOptimiserRuleId17: defineExperiment('funnel_optimiser_ruleid_17'),
  funnelOptimiserRuleId18: defineExperiment('funnel_optimiser_ruleid_18'),
  croTierProgressionOfferPage: defineExperiment('cro_tier_progression_offer_page'),
  paymentsLpc50PercentDepositSchedule: defineExperiment('payments_lpc_50_percent_deposit_schedule'),
  lereBedbankSimilarOffer: defineExperiment('lere_bedbank_similar_offer'),
  croSuggestedPackageHighInMobilePage: defineExperiment('cro_suggested_package_high_in_mobile_page'),
  opexLesaV2Assistant: defineExperiment(
    'opex_lesa_v2_assistant_ab_test',
    ['V1', 'V2'] as const,
  ),
  opexLesaNlsOffersCarousel: defineExperiment('opex_lesa_nls_offers_carousel_ab_test'),
  mobileWebOfferPageFlashCalendarSkip: defineExperiment('mobile_web_offer_page_flash_calendar_skip'),
  cruiseOfferPageEarlyTravellerSelection: defineExperiment('cruise_offer_page_early_traveller_selection'),
  cruiseLandingPageCarouselRerankingByCtr: defineExperiment(
    'cruises-carousel-rerank-ctr',
    ['control', 'variant'] as const,
  ),
  opexCheckoutOptionalGuestTravellerForms: defineExperiment('opex_checkout_optional_guest_traveller_forms'),
  opexCheckoutTravellerCardShareBooking: defineExperiment('opex_checkout_traveller_card_share_booking'),
  croCheckoutCancellationPolicyTimeline: defineExperiment('cro_checkout_cancellation_policy_timeline_2'),
  croCheckoutExperiencesUpsellRedesign: defineExperiment(
    'cro_checkout_experiences_upsell_redesign',
    ['control', 'variant'] as const,
  ),
  croNewFlightUpsellTest: defineExperiment('cro_new_flight_upsell_test'),
  internationalCheckoutFlow: defineExperiment('international_checkout_flow'),
  paymentsLpp50PercentDepositSchedule: defineExperiment('payments_lpp_50_percent_deposit_schedule'),
  croCheckoutProgressStepper: defineExperiment('cro_checkout_progress_stepper'),
  croPricePerNightToggleOnSearch: defineExperiment('cro_price_per_night_toggle_on_search'),
  /**
   * Controls visibility of the points burn UI on search results pages (toggle + slider).
   * This does NOT affect points burn at checkout, which has its own separate enablement logic.
   */
  loyaltySocieteBurn: defineExperiment('loyalty_societe_burn'),
  searchSalesCtrPaidTrafficRanking: defineExperiment('search_sales_ctr_paid_traffic_ranking'),
  searchLowViewDestinationsSalesCtrRanking: defineExperiment('search_low_view_destinations_sales_ctr_ranking'),
  croOfferPageHighlightTags: defineExperiment('cro_offer_page_highlight_tags'),
  croRefundPolicyRadioBtnsBelowInclusions: defineExperiment('cro_refund_policy_radio_btns_below_inclusions'),
  croMobileWebPackageSelectCta: defineExperiment('cro_mobile_web_package_select_cta'),
  croSppCollapsedPackages: defineExperiment(
    'cro_spp_collapsed_packages',
    ['control', 'collapsed', 'expanded'] as const,
  ),
  sppIncludeBundles: defineExperiment('spp_include_bundles'),
  searchSppAlternativeOfferGrouping: defineExperiment('search_spp_alternative_offer_grouping'),
  nhaHomesAndVillasToggleInSearch: defineExperiment('nha_homes_and_villas_toggle_in_search'),
  searchTilePropertyNearby: defineExperiment('search_tile_property_nearby'),
  croOfferNearbyLocations: defineExperiment('cro_offer_nearby_locations_2'),
  commsCompactMobileSearchTile: defineExperiment('comms_compact_mobile_search_tile'),
  sppBedbankPromos: defineExperiment('spp_bedbank_promos'),
  tripAiTripPlannerOnFlightsPage: defineExperiment('trip_ai_trip_planner_on_flights_page'),
  croMobileWebOfferHeader: defineExperiment('cro_mobile_web_offer_header'),
  croPcmTestimonialOnOfferPage: defineExperiment('cro_pcm_testimonial_on_offer_page'),
  croSoldOutFlashPackagesState: defineExperiment('cro_sold_out_flash_packages_state'),
  croSocialProofRecentPurchase: defineExperiment('cro_social_proof_recent_purchase_v2'),
  lereEndingSoonRanker: defineExperiment('lere_ending_soon_ranker'),
  croOneClickBookingRestoration: defineExperiment('cro_one_click_booking_restoration'),
  croRelativePricingComparison: defineExperiment('cro_relative_pricing_comparison', ['control', 'variant_a', 'variant_b'] as const),
  croFacilitiesRework: defineExperiment('cro_facilities_rework'),
  experiencesSearchDiscoveryRevamp: defineExperiment('experiences_search_discovery_revamp'),
  thereNoPlaceLikeHomePopup: defineExperiment('trip_planner_popup', ['control', 'variant'] as const),
  sppUpliftLpcRates: defineExperiment('spp_uplift_lpc_rates', ['control', 'variant_a', 'variant_b'] as const),
} as const

export type OptimizelyExperiments = typeof Experiments[keyof typeof Experiments]['id']

// TODO: Remove this compatibility object once all consumers import `Experiments` directly.
export const OptimizelyExperiments = Object.fromEntries(
  Object.entries(Experiments).map(([key, experiment]) => [key, experiment.id]),
) as { readonly [K in keyof typeof Experiments]: typeof Experiments[K]['id'] }

export enum OptimizelyFeatureFlags {
  luxLoyaltyFeatureToggle = 'lux_loyalty_feature_toggle',
  luxPlusFeatureToggle = 'luxplus_feature_toggle',
  luxPlusToursToggle = 'luxplus_tours_toggle',
  luxPlusDiscountedInsuranceBenefit = 'lux_plus_discounted_insurance_benefit',
  luxPlusDiscountedBookingProtectionBenefit = 'lux_plus_discounted_booking_protection_benefit',
  luxPlusLppPricingEnabled = 'luxplus_lpp_pricing_enabled',
  luxPlusCruiseInclusionsEnabled = 'lux_plus_cruise_inclusions',
  luxPlusDiscountedCruise = 'lux_plus_discounted_cruise',
  luxPlusDiscountedExperiencesBenefit = 'lux_plus_discounted_experiences_benefit',
  allowPhoneNumberLogin = 'allow_phone_number_login',
  promoAppBanner = 'app_banner_targeted_delivery',
  CSAgentsAutoQuote = 'cro_cs_agents_auto_quote',
  toursRequestAgentCallbackEnabled = 'csx_tours_request_agent_callback',
  hotelsAndVillasRequestAgentCallbackEnabled = 'csx_hotels_and_villas_request_agent_callback',
  upsellTilesFeatureToggle = 'cro_upsell_on_homepage_and_my_escapes',
  croTravelPreferencesModalEnabled = 'cro_travel_preferences_modal',
  opexTermsAndConditionsModal = 'terms_and_conditions_modal',
  croWebPushNotificationPrompt = 'cro_web_push_notification_prompt',
  opexFraudOrdersOtp = 'opex_fraud_orders_otp',
  payIdTargetedRelease = 'pay_id_targeted_release',
  carHireBundledDiscount = 'car_hire_bundled_discount',
  lereDestinationModelEnabled = 'lere_personalised_destinations',
  lereOfferPageRecommendationsEnabled = 'lere_offer_page',
  lereHeroCarouselEnabled = 'lere_hero_carousel',
  opexHotelAddExtraGuestsEnabled = 'hotel_add_extra_guests',
  opexLPCAddExtraGuestsEnabled = 'lpc_add_extra_guests',
  flightsEnableSabreContent = 'flights_enable_sabre_content',
  flightsLuxPlusPricing = 'flights_lux_plus_pricing',
  flightsBundledLuxPlusPricing = 'flights_bundled_lux_plus_pricing',
  flightsLuxPlusPreview = 'flights_lux_plus_preview',
  loyaltyLuxPlusRenewalReward = 'loyalty_lux_plus_renewal_reward',
  loyaltyPointsDeductionWarning = 'loyal_points_deduction_warning',
  croInsuranceXCoverEnabled = 'cro_insurance_xcover_enabled',
  croPayAtProperty = 'cro_pay_at_property',
  croInclusionValidity = 'cro_inclusion_validity',
  croPackageUpgradeLibEnabled = 'cro_package_upgrade_lib_enabled',
  standaloneFlightLoungePassExperiencesEnabled = 'standalone_flight_lounge_pass_experiences_enabled',
  merchandiseLaunch = 'merchandise_launch',
  loyaltyPurchasePointsEnabled = 'loyalty_points_purchase',
  opexBookingAuthority = 'opex_booking_authority',
  opexLESAConditionalBubble = 'opex_lesa_conditional_bubble',
  opexBookingAuthorityManageAccess = 'opex_booking_authority_manage_access',
  luxRow = 'lux_row',
  maldivesCharterRewardSeats = 'maldives_charter_reward_seats',
  croWrappedModalTravelled = 'cro_wrapped_modal',
  croWrappedModalAll = 'cro_wrapped_modal_all',
  croDisplaySimilarAccounts = 'cro_display_similar_accounts',
  randomizeExperiencesOffers = 'randomize_experiences_offers',
  flightsDateGrid = 'flights_date_grid',
  flightsDateGridStandalone = 'flights_date_grid_standalone',
  disallowPartialFlightsSeatSelection = 'disallow_partial_flights_seat_selection',
  loyaltyLuxplusLandingPage = 'loyalty_luxplus_landing_page',
  childrenFlyFree = 'children_fly_free',
  flightsPriceAlerts = 'flights_price_alerts',
  croPassportCardExclusiveCheckout = 'cro_passport_card_exclusive_checkout',
  loyaltyEsimBenefit = 'loyalty_esim_benefit',
  searchTypeaheadGeoTypeIndexed = 'search_typeahead_geo_type_indexed',
  croOnlyInAustraliaCarousel = 'cro_only_in_australia_carousel',
  flightsFareSense = 'flights_fare_sense',
  opexBookAgain = 'opex_book_again',
  opexSelfServiceNameChange = 'opex_self_service_name_change',
  aiOpsSpoofLaunchPadWhitelist = 'ai-ops-spoof-launch-pad-whitelist',
  leLivePopup = 'le_live_popup',
  experiencesSearchAttractions = 'experiences_search_attractions',
  maldivesCharterFnfPage = 'maldives_charter_fnf_page',
  maldivesCharterReturnFlightFree = 'maldives_charter_return_flight_free',
  charterLaunchOffer100 = 'charter_launch_offer_100',
  noPlaceLikeHomeCampaign = 'no_place_like_home_campaign',
  sppMultiIndividualRoom = 'spp_multi_individual_room',
  hotelPropertyTypeFilter = 'hotel_property_type_filter',
  flightsSelfCancellation = 'flights_self_cancellation',
  flightsSelfDateChange = 'flights_self_date_change',
  tripChatMobile = 'trip_chat_mobile',
}
